import React, { useEffect, useMemo } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useAppSelector } from './redux/hooks';
import SidebarLayout from './layouts/SidebarLayout';
import PageNotFound from './components/PageNotFound';
import PermissionService, { ACTION, ROLE } from './services/permission-service';
import {
  HelpOutline as FaqIcon,
  Announcement as AnnouncementIcon,
  People as UsersIcon,
  ContactPage as ContactIcon,
  Message as MessageIcon,
  MiscellaneousServices as ServicesIcon,
} from '@mui/icons-material';
import FaqListing from './pages/faqs/faq-listing';
import AnnouncementListing from './pages/announcements/announcement-listing';
import UserListing from './pages/users/user-listing';
import EmbassyMessageListing from './pages/embassy-messages/embassy-message-listing';
import ServiceListing from './pages/services/service-listing';
import ServiceDetails from './pages/services/service-details';
import { NewspaperIcon } from 'lucide-react';
import NewsListing from './pages/News/news-listing';
import ContactListing from './pages/Contacts/contact-listing';
import ContactDetails from './pages/Contacts/contact-details';

interface RouteConfig {
  path: string;
  element: React.ReactElement;
  title: string;
  permission?: ACTION;
  exact?: boolean;
}

const routeConfigs: RouteConfig[] = [
  {
    path: '/contacts/:id',
    element: (
        <ContactDetails />
    ),
    title: 'Contact Details',
    permission: ACTION.VIEW_ALL_CONTACTS
  },
  {
    path: '/contacts',
    element: (
        <ContactListing />
    ),
    title: 'Contact Management',
    permission: ACTION.VIEW_ALL_CONTACTS
  },
  {
    path: '/users',
    element: (
        <UserListing />
    ),
    title: 'User Management',
    permission: ACTION.VIEW_ALL_USERS
  },
  {
    path: '/faqs',
    element: (
        <FaqListing />
    ),
    title: 'FAQ Management',
    permission: ACTION.VIEW_ALL_FAQS
  },
  {
    path: '/announcements',
    element: (
        <AnnouncementListing />
    ),
    title: 'Announcement Management',
    permission: ACTION.VIEW_ALL_ANNOUNCEMENTS
  },
  {
    path: '/embassy-messages',
    element: (
        <EmbassyMessageListing />
    ),
    title: 'Embassy Messages Management',
    permission: ACTION.VIEW_ALL_EMBASSY_MESSAGES
  },
  {
    path: '/services',
    element: (
        <ServiceListing />
    ),
    title: 'Service Management',
    permission: ACTION.VIEW_ALL_SERVICES
  },
  {
    path: '/services/:serviceId',
    element: (
        <ServiceDetails />
    ),
    title: 'Service Details',
    permission: ACTION.VIEW_ALL_SERVICES
  },
    {
    path: '/news',
    element: (
        <NewsListing />
    ),
    title: 'News Management',
    permission: ACTION.VIEW_ALL_NEWS
  },
];

interface SidebarCategory {
  id: string;
  label: string;
  order: number;
}

const SIDEBAR_CATEGORIES: SidebarCategory[] = [
  { id: 'main', label: 'Main', order: 1 },
];

export const Protected: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.user);
  const isAuthed = user.isAuthenticated;

  useEffect(() => {
    if (!isAuthed) {
      navigate('/login', { replace: true });
    }
  }, [isAuthed, navigate]);

const sidebarLinks = useMemo(() => {
  const role = user.user?.role as ROLE;
  const perm = PermissionService.getInstance();

  const links = [
    perm.isAllowed(ACTION.VIEW_ALL_CONTACTS, role) && {
      id: 'contacts',
      icon: <ContactIcon />,
      label: 'Contacts',
      route: '/contacts',
      visible: true,
      category: 'main',
      order: 1
    },
    perm.isAllowed(ACTION.VIEW_ALL_USERS, role) && {
      id: 'users',
      icon: <UsersIcon />,
      label: 'Users',
      route: '/users',
      visible: true,
      category: 'main',
      order: 2
    },
    perm.isAllowed(ACTION.VIEW_ALL_FAQS, role) && {
      id: 'faqs',
      icon: <FaqIcon />,
      label: 'FAQs',
      route: '/faqs',
      visible: true,
      category: 'main',
      order: 3
    },
    perm.isAllowed(ACTION.VIEW_ALL_ANNOUNCEMENTS, role) && {
      id: 'announcements',
      icon: <AnnouncementIcon />,
      label: 'Announcements',
      route: '/announcements',
      visible: true,
      category: 'main',
      order: 4
    },
    perm.isAllowed(ACTION.VIEW_ALL_EMBASSY_MESSAGES, role) && {
      id: 'embassy-messages',
      icon: <MessageIcon />,
      label: 'Embassy Messages',
      route: '/embassy-messages',
      visible: true,
      category: 'main',
      order: 5
    },
    perm.isAllowed(ACTION.VIEW_ALL_SERVICES, role) && {
      id: 'services',
      icon: <ServicesIcon />,
      label: 'Services',
      route: '/services',
      visible: true,
      category: 'main',
      order: 6
    },
    perm.isAllowed(ACTION.VIEW_ALL_SERVICES, role) && {
      id: 'news',
      icon: <NewspaperIcon />,
      label: 'News',
      route: '/news',
      visible: true,
      category: 'main',
      order: 6
    },
  ].filter(Boolean);

  return links.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
}, [user.user?.role]);


  const layoutConfig = useMemo(() => ({
    sidebarWidth: { collapsed: 72, expanded: 300 },
    headerHeight: 64,
    showTabs: false,
    showUserInfo: true,
    allowSidebarToggle: true,
    showSearchBar: false,
    showNotifications: false, 
    theme: "dark" as "dark",
  }), []);

  const enhancedUser = useMemo(() => ({
    name: user.user?.name || 'Unknown User',
    email: user.user?.email || '',
    role: user.user?.role || 'user',
    initials: user.user?.name 
      ? user.user.name.split(' ').map(n => n[0]).join('').toUpperCase() 
      : 'UN',
  }), [user.user]);

  if (!isAuthed) {
    return null;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <SidebarLayout
            links={sidebarLinks}
            user={enhancedUser}
            config={layoutConfig}
          />
        }
      >
        {routeConfigs.map((routeConfig, index) => (
          <Route 
            key={`${routeConfig.path}-${index}`} 
            path={routeConfig.path} 
            element={routeConfig.element} 
          />
        ))}
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Routes>
  );
};